import PaymentIcon from '@library/components/payment-icon'
import { Button, MenuOutlined, Switch, Tooltip } from 'gokwik-ui-kit'
import { Config, ConfigDetailsInterface } from '@library/utilities/interface'
import { useEffect, useState } from 'react'
import { PAYMENT_METHODS } from '@library/utilities/constants/constants'

interface PaymentMethodProps {
    configs: Config[]
    enabledMethods: Config
    handleCustomisation: (config: ConfigDetailsInterface) => void
    updateConfigValue: (config: Config, value: any) => void
}

// Like wallet-airtel, wallet-phonepe, bnpl-phonepe, emi-phonepe etc.
const methodsWithInstruments = [PAYMENT_METHODS.WALLET, PAYMENT_METHODS.BNPL, PAYMENT_METHODS.EMI, PAYMENT_METHODS.PAY_IN_X]
const methodMapping = {
    cards: ['cc', 'dc'],
    pay_in_x: [PAYMENT_METHODS.SIMPLPAYIN3]
}

export default function PaymentMethod({
    configs,
    handleCustomisation,
    enabledMethods,
    updateConfigValue,
}: PaymentMethodProps) {
    const [buttonDetails, setButtonDetails] = useState({ buttonTitle: '', name: '' })

    useEffect(() => {
        configs?.forEach((config) => {
            if (config.name === 'button_title') {
                setButtonDetails((prev) => ({
                    ...prev,
                    name: config.subcategory,
                    buttonTitle: config.subcategoryTitle as string,
                }))
            }
        })
    }, [configs])

    const checkIfMethodHasInstruments = (name: string) => {
        return methodsWithInstruments.includes(name)
    }

    const handleInstrumentCustomisation = (name: string, configs: Config[]) => {
        const newConfigs = {
            instruments: [enabledMethods],
            [name]: configs,
        }
        handleCustomisation({ config: newConfigs, name: configs[0]?.categoryTitle })
    }

    const handleMethodVisibility = ({ methodName, configs, isEnabled }) => {
        if (checkIfMethodHasInstruments(methodName)) {
            handleInstrumentCustomisation(methodName, configs)
            return
        }
        if (methodName === PAYMENT_METHODS.NATIVE) {
            const getNativeMethod = configs.find((config) => config.name === 'enable_native_redirection')
            updateConfigValue(getNativeMethod, isEnabled)
            return
        }
        methodName = methodMapping[methodName] || [methodName]
        const allMethods = (enabledMethods?.value as Array<string>) ?? []
        const updatedValue = isEnabled
            ? [...allMethods, ...methodName]
            : allMethods.filter((method) => !methodName.includes(method))
        updateConfigValue(enabledMethods, updatedValue)
    }

    const handleCustomizationClick = ({ methodName, configs }) => {
        if (checkIfMethodHasInstruments(methodName)) {
            handleInstrumentCustomisation(methodName, configs)
            return
        }
        handleCustomisation({
            config: { [methodName]: configs },
            name: configs[0]?.categoryTitle,
        })
    }

    const checkIfMethodIsEnabled = (buttonName: string, enabledMethods: string[] = []) => {
        if (buttonName === PAYMENT_METHODS.CARDS) {
            return enabledMethods.includes('cc') || enabledMethods.includes('dc')
        } else if (buttonName === PAYMENT_METHODS.BHARATX) {
            return enabledMethods.includes(PAYMENT_METHODS.BHARATX_PAY)
        } else if (buttonName === PAYMENT_METHODS.EMI) {
            return enabledMethods.some((method) => /^emi-/.test(method))
        } else if (buttonName === PAYMENT_METHODS.BNPL) {
            return enabledMethods.some((method) => /^bnpl-/.test(method))
        } else if (buttonName === PAYMENT_METHODS.WALLET) {
            return enabledMethods.some((method) => /^wallet-/.test(method))
        } else if (buttonName === PAYMENT_METHODS.NATIVE) {
            const getNativeMethod = configs.find((config) => config.name === 'enable_native_redirection')
            return getNativeMethod?.value as boolean ?? false
        } else if (buttonName === PAYMENT_METHODS.PAY_IN_X) {
            return enabledMethods.includes(PAYMENT_METHODS.SIMPLPAYIN3)
        } else {
            return enabledMethods.includes(buttonName)
        }
    }

    return (
        <div className='p-4 border border-gray-300 border-solid rounded-lg w-full overflow-auto'>
            <div className='flex items-center gap-3'>
                <MenuOutlined />

                <div className='flex items-center gap-2 flex-1'>
                    <div className='w-[1.52rem] h-[1.52rem] overflow-hidden'>
                        <PaymentIcon fill='black' className={`payment-btn-icon ${configs?.[0]?.subcategory}`} />
                    </div>
                    <div className='flex flex-col'>
                        <h2 className='text-sm font-medium'>
                            {buttonDetails.name === PAYMENT_METHODS.COD ? 'COD / Partial COD' : buttonDetails.buttonTitle}
                        </h2>
                    </div>
                </div>

                <Tooltip title={buttonDetails.name === PAYMENT_METHODS.COD ? 'Reach out to Gokwik team for updating COD Status' : ''}>
                    <Switch
                        size='small'
                        checked={checkIfMethodIsEnabled(buttonDetails.name, enabledMethods?.value as Array<string>)}
                        onChange={(isEnabled) =>
                            handleMethodVisibility({
                                methodName: buttonDetails.name,
                                configs,
                                isEnabled,
                            })
                        }
                        disabled={buttonDetails.name === PAYMENT_METHODS.COD}
                    />
                </Tooltip>
                <Button
                    variant='default'
                    className='border-gray-400 text-xs p-2'
                    onClick={() =>
                        handleCustomizationClick({
                            methodName: buttonDetails.name,
                            configs,
                        })
                    }
                >
                    Customise
                </Button>
            </div>
        </div>
    )
}
